import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { createTodo } from "@/tasks/services";
import { queryKeysTasks } from "@/tasks/config";

import type { CreateTodoParams, CreateTodoResponse, Todo } from "@/tasks/types";

interface CreateTodoContext {
  previousTodos?: Todo[];
}

const useCreateTodo = (): UseMutationResult<
  CreateTodoResponse,
  Error,
  CreateTodoParams
> => {
  const queryClient = useQueryClient();

  return useMutation<CreateTodoResponse, Error, CreateTodoParams>({
    mutationFn: (params: CreateTodoParams) => createTodo(params),
    onMutate: async (newTodo: CreateTodoParams) => {
      await queryClient.cancelQueries({ queryKey: queryKeysTasks.lists() });

      const previousTodos = queryClient.getQueryData<Todo[]>(
        queryKeysTasks.list({}),
      );

      queryClient.setQueryData<Todo[]>(queryKeysTasks.list({}), (old = []) => [
        {
          ...newTodo,
          id: Date.now(), // ID temporário
          completed: newTodo.completed ?? false,
        },
        ...old,
      ]);

      return { previousTodos };
    },

    onError: (error, _, context) => {
      const typedContext = context as CreateTodoContext | undefined;
      if (typedContext?.previousTodos) {
        queryClient.setQueryData(
          queryKeysTasks.list({}),
          typedContext.previousTodos,
        );
      }

      console.error("Erro ao criar todo:", error);
    },

    onSuccess: (createdTodo) => {
      queryClient.invalidateQueries({ queryKey: queryKeysTasks.lists() });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeysTasks.lists() });
    },
  });
};

export { useCreateTodo };
